import { useEffect, useState } from "react";
import { useI18n, type StringKey } from "../i18n";
import { ProfileAction, type Action } from "./ProfileActions";
import { CustomDetails } from "../components/CustomDetails";
import { EmployeeLeave } from "./Leave";
import { loadDocuments, loadEmployee } from "../data";
import { EmploymentBadge } from "../components/Badges";
import { DocumentList } from "./Documents";
import type { Assignment, DirectoryRow, DocRow, Person, Workspace } from "../types";

export function EmployeeProfile({ ws, employmentId, onBack }: { ws: Workspace; employmentId: string; onBack?: () => void }) {
  const { t, pick } = useI18n();
  const [data, setData] = useState<{ row: DirectoryRow; person: Person; history: Assignment[] } | null>(null);
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [savedNote, setSavedNote] = useState(false);
  const canManage = ws.permissions.has("employees.manage");
  const canSeeDocs = ws.permissions.has("documents.read") && ws.enabled.has("documents");

  async function refreshDocs() {
    if (!canSeeDocs) return;
    const all = await loadDocuments(ws.membership.company_id);
    setDocs(all.filter((d) => d.employment_id === employmentId));
  }

  function refresh() {
    loadEmployee(employmentId)
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : t("errorGeneric")));
  }

  useEffect(() => {
    refresh();
    void refreshDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employmentId]);

  if (error) return <div className="page"><p className="alert danger" role="alert">{error}</p></div>;
  if (!data) return <div className="page"><p className="muted">{t("loading")}</p></div>;
  const { row, person, history } = data;

  const item = (label: string, value: string | null | undefined, mono = false) => (
    <div className="kv">
      <dt>{label}</dt>
      <dd className={mono ? "mono" : ""}>{value || "—"}</dd>
    </div>
  );

  return (
    <div className="page">
      {onBack && <button type="button" className="link back" onClick={onBack}>← {t("backToList")}</button>}
      <header className="profile-head">
        <div className="avatar" aria-hidden="true">{person.first_name[0]}{person.last_name[0]}</div>
        <div>
          <h1 className="display">{pick(row.full_name, row.full_name_ar)}</h1>
          <p className="muted">
            {pick(row.job_title ?? "", row.job_title_ar)}
            {row.department_name ? ` · ${pick(row.department_name, row.department_name_ar)}` : ""}
          </p>
          <div className="row">
            <EmploymentBadge status={row.status} />
            <span className="mono muted">{row.employee_number}</span>
          </div>
        </div>
      </header>

      {canManage && !action && (
        <div className="row action-bar">
          <button type="button" className="btn secondary small" onClick={() => setAction("edit")}>{t("editDetails")}</button>
          {row.status !== "terminated" && (
            <>
              <button type="button" className="btn secondary small" onClick={() => setAction("job")}>{t("changeJob")}</button>
              <button type="button" className="btn secondary small" onClick={() => setAction("manager")}>{t("changeManager")}</button>
              <button type="button" className="btn secondary small danger-outline" onClick={() => setAction("end")}>{t("endEmployment")}</button>
            </>
          )}
          {savedNote && <span className="badge success">{t("done")}</span>}
        </div>
      )}
      {action && (
        <ProfileAction ws={ws} action={action} row={row} person={person}
          onClose={() => setAction(null)}
          onSaved={() => { setAction(null); setSavedNote(true); refresh(); void refreshDocs(); }} />
      )}

      <div className="profile-grid">
        <section className="card">
          <h2>{t("sectionJob")}</h2>
          <dl className="kv-list">
            {item(t("hireDate"), row.hire_date, true)}
            {item(t("probationEnd"), row.probation_end_date, true)}
            {row.termination_date && item(t("terminated"), row.termination_date, true)}
            {item(t("branch"), row.branch_name ? pick(row.branch_name, row.branch_name_ar) : null)}
            {item(t("manager"), row.manager_name)}
            {item(t("grade"), row.grade)}
            {item(t("fte"), row.fte != null ? String(row.fte) : null, true)}
            {item(t("workEmail"), row.work_email)}
          </dl>
        </section>
        <section className="card">
          <h2>{t("sectionPerson")}</h2>
          <dl className="kv-list">
            {item(t("gender"), person.gender ? t(person.gender) : null)}
            {item(t("dateOfBirth"), person.date_of_birth, true)}
            {item(t("nationality"), person.nationality === "EG" ? t("egyptian") : person.nationality)}
            {item(t("nationalId"), person.national_id, true)}
            {item(t("mobile"), person.mobile, true)}
            {item(t("personalEmail"), person.personal_email)}
          </dl>
        </section>
      </div>

      <CustomDetails companyId={ws.membership.company_id} personId={person.id} employmentId={row.employment_id} canEdit={canManage} />

      {ws.enabled.has("leave") && (ws.permissions.has("leave.read") || ws.teamSize > 0 || ws.membership.employment_id === employmentId) && (
        <EmployeeLeave ws={ws} employmentId={employmentId} />
      )}

      <section className="card">
        <h2>{t("jobHistory")}</h2>
        <div className="table-wrap">
          <table>
            <thead><tr><th>{t("from")}</th><th>{t("to")}</th><th>{t("jobTitle")}</th><th>{t("change")}</th></tr></thead>
            <tbody>
              {history.map((a) => (
                <tr key={a.id}>
                  <td className="mono">{a.start_date}</td>
                  <td className="mono">{a.end_date ?? t("current")}</td>
                  <td>{pick(a.job_title, a.job_title_ar)}</td>
                  <td className="muted">{t(`reason_${a.change_reason}` as StringKey)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {canSeeDocs && (
        <section className="card">
          <h2>{t("documents")}</h2>
          <DocumentList ws={ws} rows={docs} onChanged={refreshDocs} showEmployee={false} />
        </section>
      )}
    </div>
  );
}
